import * as XLSX from 'xlsx';

/** @typedef {'error' | 'warning' | 'info'} ValidationSeverity */
/** @typedef {'table_structure' | 'category_axis' | 'invalid_category' | 'period_type' | 'unit' | 'other'} IssueTypeKey */

export const ISSUE_TYPES = {
	TABLE_STRUCTURE: 'table_structure',
	CATEGORY_AXIS: 'category_axis',
	INVALID_CATEGORY: 'invalid_category',
	PERIOD_TYPE: 'period_type',
	UNIT: 'unit',
	OTHER: 'other',
};

export const ISSUE_TYPE_LABELS = {
	[ISSUE_TYPES.TABLE_STRUCTURE]: 'Table / dimension structure mismatch',
	[ISSUE_TYPES.CATEGORY_AXIS]: 'Invalid category or axis / member',
	[ISSUE_TYPES.INVALID_CATEGORY]: 'Invalid category value',
	[ISSUE_TYPES.PERIOD_TYPE]: 'Wrong period type (instant vs duration)',
	[ISSUE_TYPES.UNIT]: 'Invalid unit',
	[ISSUE_TYPES.OTHER]: 'Other',
};

const FIELD_LABELS = {
	RemarkForConsumerComplaints: 'Remark for consumer complaints',
	ConsumerComplaints: 'Consumer complaints',
	WasteType: 'Waste type',
	UnfairTradePractices: 'Unfair trade practices',
	CapitalMarket: 'Capital market',
	in_capmkt: 'Capital market',
	'in-capmkt': 'Capital market',
};

const CONTEXT_SECTION_PATTERNS = [
	{ regex: /D[_-]?SectionA/i, section: 'Section A' },
	{ regex: /D[_-]?SectionB/i, section: 'Section B' },
	{ regex: /D[_-]?SectionC/i, section: 'Section C' },
	{ regex: /D[_-]?Principle(\d+)/i, section: (m) => `Principle ${m[1]}` },
	{ regex: /Principle(\d+)/i, section: (m) => `Principle ${m[1]}` },
	{ regex: /UnfairTradePractices/i, section: 'Principle 9' },
	{ regex: /ConsumerComplaints/i, section: 'Principle 9' },
	{ regex: /WasteType/i, section: 'Principle 6' },
];

/**
 * @param {string} camelOrSnake
 * @returns {string}
 */
export function humanizeFieldName(camelOrSnake) {
	if (!camelOrSnake) return '';
	const trimmed = String(camelOrSnake).trim();
	if (FIELD_LABELS[trimmed]) return FIELD_LABELS[trimmed];
	const spaced = trimmed
		.replace(/[-_]+/g, ' ')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * @param {string} msg
 * @returns {IssueTypeKey}
 */
export function classifyIssueType(msg) {
	const lower = (msg || '').toLowerCase();
	if (/dimensionally not valid|table structure|hypercube|dimension.*mismatch/.test(lower)) {
		return ISSUE_TYPES.TABLE_STRUCTURE;
	}
	if (/not an appropriate dimension|typedmember|axis.*member|member.*axis/.test(lower)) {
		return ISSUE_TYPES.CATEGORY_AXIS;
	}
	if (/not a global member|invalid.*categor|domain member/.test(lower)) {
		return ISSUE_TYPES.INVALID_CATEGORY;
	}
	if (/period type|duration conflict|instant.*duration|duration.*instant/.test(lower)) {
		return ISSUE_TYPES.PERIOD_TYPE;
	}
	if (/illegal measure|invalid unit|\bunit\b.*not|measure.*invalid/.test(lower)) {
		return ISSUE_TYPES.UNIT;
	}
	return ISSUE_TYPES.OTHER;
}

/**
 * @param {string} text
 * @returns {string}
 */
export function parseSectionFromText(text) {
	if (!text) return 'General';
	const haystack = String(text);
	for (const { regex, section } of CONTEXT_SECTION_PATTERNS) {
		const m = haystack.match(regex);
		if (m) {
			return typeof section === 'function' ? section(m) : section;
		}
	}
	return 'General';
}

/**
 * @param {string} msg
 * @returns {string}
 */
function extractContextId(msg) {
	const ctxMatch =
		msg.match(/context\s+[`'"]?([^`'"\s,)]+)/i) ||
		msg.match(/\b(D[_A-Za-z0-9-]+)\b/) ||
		msg.match(/contextRef[=:\s]+[`'"]?([^`'"\s,)]+)/i);
	return ctxMatch ? ctxMatch[1] : '';
}

/**
 * @param {string} msg
 * @returns {string}
 */
function extractElementName(msg) {
	const factMatch = msg.match(/Fact\s+\{?([^}\s,:()]+)/i);
	if (factMatch) {
		const raw = factMatch[1].replace(/[{}]/g, '').split(',')[0].trim();
		if (raw && !/^in[-_]?/i.test(raw)) return raw;
	}
	const qnameMatch = msg.match(/[`'"]?([A-Za-z][\w:-]*:[\w.-]+)[`'"]?/);
	if (qnameMatch) return qnameMatch[1].split(':').pop();
	const elementMatch = msg.match(/\belement\s+[`'"]?(\w+)/i);
	if (elementMatch) return elementMatch[1];
	const inMatch = msg.match(/in[-_]?([\w]+)/i);
	if (inMatch) return inMatch[0];
	const nameMatch = msg.match(/\b([A-Z][a-z]+(?:[A-Z][a-z]+)+)\b/);
	return nameMatch ? nameMatch[1] : '';
}

/**
 * @param {string} msg
 * @returns {ValidationSeverity}
 */
function extractSeverity(msg) {
	const upper = (msg || '').trim();
	if (/^WARNING:/i.test(upper) || /\bwarning\b/i.test(upper)) return 'warning';
	if (/^INFO:/i.test(upper) || /\binfo\b/i.test(upper)) return 'info';
	return 'error';
}

/**
 * @param {string} msg
 * @param {ValidationSeverity} [severityOverride]
 * @returns {{
 *   severity: ValidationSeverity,
 *   element: string,
 *   contextId: string,
 *   issueType: IssueTypeKey,
 *   section: string,
 *   friendlyField: string,
 *   friendlySummary: string,
 *   message: string,
 * }}
 */
export function parseValidationMessage(msg, severityOverride) {
	const message = String(msg || '').trim();
	const severity = severityOverride || extractSeverity(message);
	const contextId = extractContextId(message);
	const element = extractElementName(message);
	const issueType = classifyIssueType(message);
	const section = parseSectionFromText(`${contextId} ${element} ${message}`);
	const friendlyField = humanizeFieldName(
		FIELD_LABELS[element] ? element : element || contextId.replace(/^D[_-]?/, '')
	);
	const issueLabel = ISSUE_TYPE_LABELS[issueType] || ISSUE_TYPE_LABELS[ISSUE_TYPES.OTHER];
	const friendlySummary = friendlyField
		? `${issueLabel} — ${friendlyField}`
		: issueLabel;

	return {
		severity,
		element,
		contextId,
		issueType,
		section,
		friendlyField: friendlyField || '—',
		friendlySummary,
		message,
	};
}

/**
 * @param {object} validation
 * @returns {boolean}
 */
export function shouldBlockXbrlDownload(validation) {
	if (!validation || typeof validation !== 'object') return false;
	const errCount =
		validation.error_count ?? (Array.isArray(validation.errors) ? validation.errors.length : 0);
	const warnCount =
		validation.warning_count ??
		(Array.isArray(validation.warnings) ? validation.warnings.length : 0);
	if (validation.is_valid === true && errCount === 0 && warnCount === 0) return false;
	return validation.is_valid === false || errCount + warnCount > 0;
}

/**
 * @param {string[]} errors
 * @param {string[]} warnings
 * @param {string[]} [info]
 * @returns {{
 *   parsed: ReturnType<typeof parseValidationMessage>[],
 *   groups: { key: string, issueType: IssueTypeKey, issueTypeLabel: string, section: string, count: number, examples: string[] }[],
 *   sectionGroups: { section: string, issueType: IssueTypeKey, issueTypeLabel: string, count: number }[],
 *   totals: { errors: number, warnings: number, info: number },
 * }}
 */
export function groupValidationIssues(errors = [], warnings = [], info = []) {
	const parsed = [];
	(errors || []).forEach((m) => parsed.push(parseValidationMessage(m, 'error')));
	(warnings || []).forEach((m) => parsed.push(parseValidationMessage(m, 'warning')));
	(info || []).forEach((m) => parsed.push(parseValidationMessage(m, 'info')));

	const groupMap = new Map();
	for (const p of parsed) {
		const key = `${p.issueType}::${p.section}`;
		if (!groupMap.has(key)) {
			groupMap.set(key, {
				key,
				issueType: p.issueType,
				issueTypeLabel: ISSUE_TYPE_LABELS[p.issueType],
				section: p.section,
				count: 0,
				examples: [],
			});
		}
		const g = groupMap.get(key);
		g.count += 1;
		if (g.examples.length < 3 && p.message && !g.examples.includes(p.message)) {
			g.examples.push(p.message);
		}
	}

	const groups = [...groupMap.values()].sort((a, b) => b.count - a.count);

	const sectionMap = new Map();
	for (const g of groups) {
		const sk = `${g.section}::${g.issueType}`;
		if (!sectionMap.has(sk)) {
			sectionMap.set(sk, {
				section: g.section,
				issueType: g.issueType,
				issueTypeLabel: g.issueTypeLabel,
				count: 0,
			});
		}
		sectionMap.get(sk).count += g.count;
	}
	const sectionGroups = [...sectionMap.values()].sort(
		(a, b) => b.count - a.count || a.section.localeCompare(b.section)
	);

	return {
		parsed,
		groups,
		sectionGroups,
		totals: {
			errors: (errors || []).length,
			warnings: (warnings || []).length,
			info: (info || []).length,
		},
	};
}

/**
 * @param {object} validation
 * @returns {ReturnType<typeof groupValidationIssues>}
 */
export function buildValidationGroups(validation) {
	return groupValidationIssues(
		validation?.errors || [],
		validation?.warnings || [],
		validation?.info || []
	);
}

/**
 * @param {object} validation
 * @returns {ArrayBuffer}
 */
export function buildValidationExcel(validation) {
	const { parsed, groups } = buildValidationGroups(validation);
	const seen = new Set();
	const detailRows = [];

	for (const p of parsed) {
		const dedupeKey = `${p.severity}|${p.message}`;
		if (seen.has(dedupeKey)) continue;
		seen.add(dedupeKey);
		detailRows.push(p);
	}

	const issuesHeader = [
		'#',
		'Severity',
		'Section',
		'Issue type',
		'Field',
		'Context',
		'Message',
	];
	const issuesData = detailRows.map((p, idx) => [
		idx + 1,
		p.severity.charAt(0).toUpperCase() + p.severity.slice(1),
		p.section,
		ISSUE_TYPE_LABELS[p.issueType],
		p.friendlyField,
		p.contextId || '—',
		p.message,
	]);

	const summaryHeader = ['Issue type', 'Section', 'Count'];
	const summaryData = groups.map((g) => [g.issueTypeLabel, g.section, g.count]);

	const wb = XLSX.utils.book_new();
	const wsIssues = XLSX.utils.aoa_to_sheet([issuesHeader, ...issuesData]);
	const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeader, ...summaryData]);
	wsIssues['!cols'] = [
		{ wch: 6 },
		{ wch: 10 },
		{ wch: 16 },
		{ wch: 36 },
		{ wch: 28 },
		{ wch: 22 },
		{ wch: 80 },
	];
	wsSummary['!cols'] = [{ wch: 36 }, { wch: 16 }, { wch: 10 }];
	XLSX.utils.book_append_sheet(wb, wsIssues, 'Validation issues');
	XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
	return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

/**
 * @param {object} validation
 * @param {string} [fileName]
 */
export function downloadValidationExcelReport(validation, fileName) {
	const buffer = buildValidationExcel(validation);
	const blob = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
	const stamp =
		fileName ||
		`BRSR_XBRL_Validation_Report_${formatReportTimestamp()}.xlsx`;
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = stamp.endsWith('.xlsx') ? stamp : `${stamp}.xlsx`;
	link.click();
	window.URL.revokeObjectURL(url);
}

/**
 * @returns {string} YYYYMMDD_HHmm
 */
export function formatReportTimestamp() {
	const d = new Date();
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}
